(() => {
  if (customElements.get('exit-intent-popup')) return;

  class ExitIntentPopup extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';
      this.dialog = this.querySelector('[data-eip-dialog]');
      this.closeButtons = this.querySelectorAll('[data-eip-close]');
      this.copyButton = this.querySelector('[data-eip-copy]');
      this.actionButton = this.querySelector('[data-eip-action]');
      this.status = this.querySelector('[data-eip-status]');
      this.abortController = new AbortController();
      this.lastFocused = null;
      this.timer = null;
      this.inactivityTimer = null;
      this.hasTriggered = false;
      this.bind();
      this.start();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      window.clearTimeout(this.timer);
      window.clearTimeout(this.inactivityTimer);
    }

    bind() {
      const options = { signal: this.abortController.signal };
      this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close('dismiss'), options));
      this.copyButton?.addEventListener('click', () => this.copyCode(), options);
      this.actionButton?.addEventListener('click', (event) => this.handleAction(event), options);
      this.dialog?.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close('dismiss');
      }, options);
      document.addEventListener('keydown', (event) => this.handleKeydown(event), options);
      document.addEventListener('mousemove', (event) => this.handlePointer(event), options);
      document.addEventListener('mouseout', (event) => this.handlePointer(event), options);
      ['pointerdown', 'keydown', 'scroll', 'touchstart'].forEach((name) => {
        document.addEventListener(name, () => this.resetInactivity(), { passive: true, signal: this.abortController.signal });
      });
      document.addEventListener('shopify:section:load', (event) => {
        if (event.target?.contains(this)) this.show(true);
      }, options);
      document.addEventListener('shopify:section:select', (event) => {
        if (event.target?.contains(this)) this.show(true);
      }, options);
    }

    start() {
      if (this.isSuppressed()) return;
      const trigger = this.dataset.trigger || 'exit';
      if (trigger === 'delay' || trigger === 'exit_and_delay') {
        this.timer = window.setTimeout(() => this.show(), this.number('delay', 12000));
      }
      if (trigger === 'inactivity' || trigger === 'exit_and_inactivity') this.resetInactivity();
    }

    handlePointer(event) {
      const trigger = this.dataset.trigger || 'exit';
      if (!['exit', 'exit_and_delay', 'exit_and_inactivity'].includes(trigger)) return;
      if (event.type === 'mousemove' && event.clientY > this.number('exitThreshold', 12)) return;
      if (event.type === 'mouseout' && event.relatedTarget !== null) return;
      if (event.clientY <= this.number('exitThreshold', 12)) this.show();
    }

    resetInactivity() {
      const trigger = this.dataset.trigger || 'exit';
      if (!['inactivity', 'exit_and_inactivity'].includes(trigger) || this.hasTriggered) return;
      window.clearTimeout(this.inactivityTimer);
      this.inactivityTimer = window.setTimeout(() => this.show(), this.number('inactivityDelay', 20000));
    }

    show(force = false) {
      if (this.hasTriggered && !force) return;
      if (!force && this.isSuppressed()) return;
      if (!force && window.scrollY < this.number('minimumScroll', 0)) return;
      this.hasTriggered = true;
      window.clearTimeout(this.timer);
      window.clearTimeout(this.inactivityTimer);
      this.lastFocused = document.activeElement;
      this.removeAttribute('hidden');
      document.documentElement.classList.add('exit-intent-popup-open');
      if (typeof this.dialog?.showModal === 'function' && !this.dialog.open) this.dialog.showModal();
      else this.dialog?.setAttribute('open', '');
      requestAnimationFrame(() => this.classList.add('is-visible'));
      this.querySelector('[data-eip-initial-focus]')?.focus({ preventScroll: true });
      this.announce(this.dataset.openAnnouncement || '');
    }

    close(reason = 'dismiss') {
      if (!this.dialog?.open && !this.classList.contains('is-visible')) return;
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('exit-intent-popup-open');
      if (reason === 'dismiss') this.rememberDismissal();
      window.setTimeout(() => {
        if (typeof this.dialog?.close === 'function' && this.dialog.open) this.dialog.close();
        else this.dialog?.removeAttribute('open');
        this.setAttribute('hidden', '');
        this.lastFocused?.focus?.({ preventScroll: true });
      }, this.number('transitionDuration', 260));
    }

    async copyCode() {
      const code = this.dataset.discountCode?.trim();
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
      } catch (_) {
        const input = document.createElement('textarea');
        input.value = code;
        input.setAttribute('readonly', '');
        input.style.position = 'fixed';
        input.style.opacity = '0';
        document.body.append(input);
        input.select();
        document.execCommand('copy');
        input.remove();
      }
      const label = this.copyButton?.querySelector('[data-eip-copy-label]');
      if (label) {
        const original = label.textContent;
        label.textContent = this.dataset.copiedLabel || original;
        window.setTimeout(() => { label.textContent = original; }, 1800);
      }
      this.announce(this.dataset.copiedAnnouncement || '');
    }

    async handleAction(event) {
      const action = this.dataset.action || 'link';
      if (action !== 'add_to_cart') {
        this.rememberConversion();
        return;
      }
      event.preventDefault();
      const variantId = this.dataset.variantId;
      if (!variantId) return;
      this.actionButton.disabled = true;
      this.classList.add('is-loading');
      try {
        const response = await fetch(this.dataset.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: this.number('quantity', 1) }] })
        });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        this.rememberConversion();
        this.announce(this.dataset.addedAnnouncement || '');
        const destination = this.dataset.afterAdd || 'cart';
        if (destination === 'checkout') window.location.assign(this.dataset.checkoutUrl || '/checkout');
        else if (destination === 'cart') window.location.assign(this.dataset.cartUrl || '/cart');
        else this.close('convert');
      } catch (error) {
        console.warn('Exit intent popup: add to cart failed.', error);
        this.announce(this.dataset.errorAnnouncement || '');
        this.classList.add('has-error');
      } finally {
        this.actionButton.disabled = false;
        this.classList.remove('is-loading');
      }
    }

    handleKeydown(event) {
      if (event.key !== 'Tab' || !this.dialog?.open) return;
      const focusable = [...this.dialog.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
        .filter((node) => !node.hidden && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    isSuppressed() {
      if (globalThis.Shopify?.designMode) return false;
      try {
        const value = JSON.parse(localStorage.getItem(this.storageKey()) || 'null');
        return value?.until > Date.now();
      } catch (_) { return false; }
    }

    rememberDismissal() {
      const hours = this.number('dismissHours', 24);
      if (hours <= 0) return;
      this.store({ until: Date.now() + hours * 3600000, type: 'dismiss' });
    }

    rememberConversion() {
      const hours = this.number('conversionHours', 168);
      if (hours <= 0) return;
      this.store({ until: Date.now() + hours * 3600000, type: 'convert' });
    }

    store(value) {
      try { localStorage.setItem(this.storageKey(), JSON.stringify(value)); } catch (_) {}
    }

    storageKey() { return `exit-intent-popup:${this.dataset.storageKey || 'default'}`; }
    number(name, fallback) { const value = Number(this.dataset[name]); return Number.isFinite(value) ? value : fallback; }
    announce(message) { if (this.status && message) this.status.textContent = message; }
  }

  customElements.define('exit-intent-popup', ExitIntentPopup);
})();
